import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  ADVANCED_MARKING_DEFAULTS,
  AI_REPLY_OPTIONS,
  FEEDBACK_SUPPORT_OPTIONS,
  FEEDBACK_AMOUNT_OPTIONS,
  FEEDBACK_AMOUNT_DEFAULT,
  normalizeRubricDifficulty,
  RUBRIC_DIFFICULTY_OPTIONS,
  SPECIFIC_COMMENT_OPTIONS,
  SUMMARY_LENGTH_OPTIONS,
  getOptionIndex,
  getOptionValue,
} from '../../utils/advancedMarking';
import {
  ControlCard,
  ControlTitle,
  ControlsGrid,
  HelperRow,
  InfoIcon,
  HelperText,
  OptionGroup,
  OptionChip,
} from './style';

const DiscreteSlider = ({
  label,
  options,
  value,
  onChange,
  disabled = false,
  id,
}) => {
  const index = getOptionIndex(options, value, options[0]?.value);
  const helper = options[index]?.helper || '';
  return (
    <ControlCard id={id}>
      <ControlTitle>{label}</ControlTitle>
      <OptionGroup $count={options.length}>
        {options.map((option, optionIndex) => (
          <OptionChip
            key={option.value}
            type="button"
            isActive={option.value === value}
            onClick={() => onChange(getOptionValue(options, optionIndex, value))}
            disabled={disabled}
          >
            {option.label}
          </OptionChip>
        ))}
      </OptionGroup>
      {helper && (
        <HelperRow>
          <InfoIcon>i</InfoIcon>
          <HelperText>{helper}</HelperText>
        </HelperRow>
      )}
    </ControlCard>
  );
};

const mapSophisticationToSupportPreference = (enabled) =>
  enabled ? 'EXEMPLARY_PERFORMANCE' : 'NEXT_LEVEL_IMPROVEMENT';

const mapSupportPreferenceToSophistication = (preference) =>
  preference === 'EXEMPLARY_PERFORMANCE';

const ADVANCED_NAV_SECTIONS = [
  {
    id: 'annotations',
    navLabel: 'Number of annotations',
    label:
      'How many in-text annotations would you like to provide students on their work?',
    optionsKey: 'specificCommentQuantity',
    options: SPECIFIC_COMMENT_OPTIONS,
  },
  {
    id: 'guidance',
    navLabel: 'Level of guidance',
    label:
      'How much guidance would you like your in-text annotations to provide?',
    optionsKey: 'aiReplyDetail',
    options: AI_REPLY_OPTIONS,
  },
  {
    id: 'summary',
    navLabel: 'Summary comment length',
    label: 'How long would you like the summary comments to be?',
    optionsKey: 'summaryLength',
    options: SUMMARY_LENGTH_OPTIONS,
  },
  {
    id: 'support',
    navLabel: 'Support response',
    label:
      'When giving feedback, what do you primarily want it to support?',
    optionsKey: 'sophisticationEnabled',
    options: FEEDBACK_SUPPORT_OPTIONS,
  },
  {
    id: 'marking',
    navLabel: 'Marking assistance',
    label: 'What is your preferred standard when awarding grades?',
    optionsKey: 'rubricDifficulty',
    options: RUBRIC_DIFFICULTY_OPTIONS,
  },
];

export const AdvancedMarkingControlsForm = ({
  controls = ADVANCED_MARKING_DEFAULTS,
  onChange,
  isDisabled = false,
  layout = 'grid',
}) => {
  const [activeSection, setActiveSection] = useState(
    ADVANCED_NAV_SECTIONS[0].id,
  );
  const sectionsRef = useRef(null);

  const updateField = (field, value) => {
    if (onChange) {
      onChange({ [field]: value });
    }
  };

  const getSectionValue = (section) => {
    if (section.optionsKey === 'sophisticationEnabled') {
      return mapSophisticationToSupportPreference(
        controls.sophisticationEnabled,
      );
    }
    if (section.optionsKey === 'rubricDifficulty') {
      return normalizeRubricDifficulty(controls.rubricDifficulty);
    }
    return controls[section.optionsKey];
  };

  const handleSectionChange = (section, value) => {
    if (section.optionsKey === 'sophisticationEnabled') {
      updateField(
        'sophisticationEnabled',
        mapSupportPreferenceToSophistication(value),
      );
      return;
    }
    updateField(section.optionsKey, value);
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const node = sectionsRef.current?.querySelector(`#amc-${sectionId}`);
    node?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (layout !== 'nav' || !sectionsRef.current) return undefined;
    const root = sectionsRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) {
          setActiveSection(visible.target.id.replace(/^amc-/, ''));
        }
      },
      { root, rootMargin: '-20% 0px -60% 0px', threshold: [0.2, 0.5, 0.8] },
    );
    ADVANCED_NAV_SECTIONS.forEach((section) => {
      const node = root.querySelector(`#amc-${section.id}`);
      if (node) observer.observe(node);
    });
    return () => observer.disconnect();
  }, [layout]);

  const sliders = ADVANCED_NAV_SECTIONS.map((section) => (
    <DiscreteSlider
      key={section.id}
      id={`amc-${section.id}`}
      label={section.label}
      options={section.options}
      value={getSectionValue(section)}
      onChange={(value) => handleSectionChange(section, value)}
      disabled={isDisabled}
    />
  ));

  if (layout === 'nav') {
    return (
      <NavLayout>
        <NavList>
          {ADVANCED_NAV_SECTIONS.map((section) => (
            <NavItem
              key={section.id}
              type="button"
              $active={activeSection === section.id}
              onClick={() => scrollToSection(section.id)}
            >
              {section.navLabel}
            </NavItem>
          ))}
        </NavList>
        <NavSections ref={sectionsRef}>{sliders}</NavSections>
      </NavLayout>
    );
  }

  return <ControlsGrid>{sliders}</ControlsGrid>;
};

export const FeedbackAmountControl = ({
  value = FEEDBACK_AMOUNT_DEFAULT,
  onChange,
  label = 'Amount of feedback',
  description = 'Controls the depth of specific and overall feedback.',
  isDisabled = false,
}) => {
  return (
    <ControlsGrid>
      <DiscreteSlider
        label={label}
        description={description}
        options={FEEDBACK_AMOUNT_OPTIONS}
        value={value}
        onChange={onChange}
        disabled={isDisabled}
      />
    </ControlsGrid>
  );
};

const NavLayout = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;
  min-height: 360px;
  align-items: flex-start;

  @media (max-width: 765px) {
    flex-direction: column;
  }
`;

const NavList = styled.div`
  width: 200px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 0;
  border-right: 1px solid rgba(201, 198, 204, 0.5);

  @media (max-width: 765px) {
    width: 100%;
    flex-direction: row;
    overflow-x: auto;
    border-right: none;
    border-bottom: 1px solid rgba(201, 198, 204, 0.5);
    padding-bottom: 8px;
  }
`;

const NavItem = styled.button`
  border: none;
  background: ${({ $active }) => ($active ? '#f1e6fc' : 'transparent')};
  color: ${({ $active }) => ($active ? '#7200e0' : '#4b464f')};
  text-align: left;
  padding: 12px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  line-height: 1.3;
  white-space: nowrap;

  &:hover {
    background: #fbf7fe;
    color: #7200e0;
  }
`;

const NavSections = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-height: 520px;
  overflow-y: auto;
  padding-right: 4px;
`;
